import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "@babackapp/ui/components/form";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import { cn } from "@babackapp/ui/lib/utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import type { PropsWithChildren } from "react";

type FormInputContainerProps = {
  label: string;
  description?: string;
  direction?: "vertical" | "horizontal";
  disabled?: string | boolean;
  disabledMessage?: boolean;
};

export default function FormInputContainer({
  label,
  description,
  direction = "vertical",
  disabled,
  children,
  disabledMessage = false,
}: PropsWithChildren<FormInputContainerProps>) {
  return (
    <DisabledReasonContainer disabled={disabled}>
      <FormItem
        className={cn(
          "flex flex-1 transition-all w-full min-w-[150px]",
          {
            vertical: "flex-col justify-between",
            horizontal: "flex-row gap-8 justify-between items-center",
          }[direction],
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {(label || description) && (
          <div>
            <FormLabel>{label}</FormLabel>
            {description && (
              <FormDescription className="my-0 py-0 max-w-[350px] pb-1">
                {description}
              </FormDescription>
            )}
          </div>
        )}
        <FormControl className="flex-1">
          <>
            {children}
            {!disabledMessage && <FormMessage />}
          </>
        </FormControl>
      </FormItem>
    </DisabledReasonContainer>
  );
}

type DisabledReasonContainerProps = {
  disabled?: string | boolean;
  align?: "start" | "center" | "end";
};

export function DisabledReasonContainer({
  children,
  align,
  disabled,
}: PropsWithChildren<DisabledReasonContainerProps>) {
  if (typeof disabled !== "string") return <>{children}</>;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align={align ?? "start"}>
        <div className="flex gap-4 w-56">
          <ExclamationTriangleIcon className="text-yellow-500 h-8 w-8" />
          <p className="text-xs font-medium text-muted-foreground leading-snug">
            {disabled}
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
